import { useEffect, useState } from 'react'
import * as z from 'zod'
import { toast } from 'sonner'
import { INTEGRATIONS } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import type { AppFolioMetadata } from '@/lib/api/integrations/appfolio.client'
import { AccountCredentialForm } from '../AccountCredentialForm'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

/**
 * AppFolio connect + edit form. This is the single canonical form component;
 * both `/settings/integrations/appfolio` and the onboarding sub-page mount
 * this exact JSX, with the same schema, same submit handler, same client
 * calls. Changes to the AppFolio connect flow happen here — one file, one
 * place, zero drift.
 */
export interface AppFolioConnectFormProps {
  /** When true, prefills from the existing integration and calls update endpoints. */
  isEditMode?: boolean
  /** Called after a successful connect or update. */
  onConnected: () => void
}

const parseAppFolioUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url)
    const match = urlObj.hostname.match(/^([^.]+)\.appfolio\.com$/)
    return match ? match[1] : null
  } catch {
    return null
  }
}

const appFolioAdditionalSchema = z.object({
  appFolioUrl: z
    .string()
    .min(1, 'AppFolio URL is required')
    .refine(
      url => {
        try {
          new URL(url)
          return true
        } catch {
          return false
        }
      },
      { message: 'Please enter a valid URL' },
    )
    .refine(url => parseAppFolioUrl(url) !== null, {
      message: 'URL must be in format: https://yourcompany.appfolio.com',
    }),
  defaultProperty: z.string().optional(),
})

export function AppFolioConnectForm({
  isEditMode = false,
  onConnected,
}: AppFolioConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [existingCredential, setExistingCredential] = useState<{
    accountIdentifier: string
  } | null>(null)
  const [appFolioUrl, setAppFolioUrl] = useState<string>('')
  const [defaultProperty, setDefaultProperty] = useState<string>('')
  const [isLoading, setIsLoading] = useState(isEditMode)

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) {
      setIsLoading(false)
      return
    }
    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const appFolio = integrations.find(
          i =>
            i.type === INTEGRATIONS.APPFOLIO.TYPE ||
            i.name === INTEGRATIONS.APPFOLIO.NAME,
        )
        if (appFolio?.connected && appFolio.accountId) {
          setExistingCredential({ accountIdentifier: appFolio.accountId })
          if (appFolio.metadata?.configId) {
            setAppFolioUrl(`https://${appFolio.metadata.configId}.appfolio.com`)
          }
          if (appFolio.metadata?.defaultProperty) {
            setDefaultProperty(appFolio.metadata.defaultProperty as string)
          }
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing credentials')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin])

  const handleSubmit = async (data: {
    accountIdentifier: string
    password: string
    appFolioUrl?: string
    defaultProperty?: string
  }) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }
    const configId = data.appFolioUrl
      ? parseAppFolioUrl(data.appFolioUrl)
      : null
    if (!configId) {
      toast.error('Invalid AppFolio URL format')
      return
    }
    const metadata: AppFolioMetadata = {
      configId,
      ...(data.defaultProperty && { defaultProperty: data.defaultProperty }),
    }
    try {
      if (isEditMode) {
        if (isSuperAdmin) {
          await integrationsClient.appFolio.updateAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.appFolio.updateCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('AppFolio credentials updated successfully!')
      } else {
        if (isSuperAdmin) {
          await integrationsClient.appFolio.createAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.appFolio.createCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('AppFolio integration connected successfully!')
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} AppFolio:`,
        error,
      )
      toast.error(
        `Failed to ${isEditMode ? 'update' : 'connect'} AppFolio integration`,
      )
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <AccountCredentialForm
      onSubmit={handleSubmit}
      accountIdentifierLabel="Email"
      accountIdentifierPlaceholder="your-appfolio-username@example.com"
      passwordLabel="Password"
      passwordPlaceholder="Enter your password"
      submitButtonText={isEditMode ? 'Update' : 'Connect'}
      initialAccountIdentifier={existingCredential?.accountIdentifier}
      additionalSchema={appFolioAdditionalSchema}
      initialValues={{ appFolioUrl, defaultProperty }}
      confirmationText={
        isEditMode
          ? 'Update your credentials for AppFolio'
          : 'Connect your credentials for AppFolio'
      }
    >
      {({ form, isSubmitting }) => (
        <>
          <FormField
            control={form.control}
            name="appFolioUrl"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>AppFolio URL</FormLabel>
                <FormControl>
                  <Input
                    placeholder="https://yourcompany.appfolio.com"
                    disabled={isSubmitting}
                    value={field.value as string}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                </FormControl>
                <FormDescription>
                  Your AppFolio portal URL (e.g.,
                  https://yourcompany.appfolio.com)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="defaultProperty"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>Default property (optional)</FormLabel>
                <FormControl>
                  <Input
                    placeholder="121 Main St, Anytown, CA"
                    disabled={isSubmitting}
                    value={field.value as string}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                </FormControl>
                <FormDescription>
                  The default property address to use for the integration
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </AccountCredentialForm>
  )
}
